import { useState } from 'react';
import { generatePath, useHistory } from 'react-router-dom';
import { t } from 'ttag';
import  classNames from 'classnames';
import queryString from 'query-string';

import CloseIcon from '@svg/close.svg';
import { PaymentMethodEnum, PaymentStatusEnum, PaymentTransactionStatusEnum } from '@newModels';
import { useRegion } from '@hook/useRegion';
import { purchaseDetails, smartPayment } from '@urls';
import { usePlatformAuthorizeInfo } from '@hook/usePlatformAuthorizeInfo';
import { getOrderPaymentPathname } from '@utils/order';
import { formatPrecisePrice } from '@utils/common';
import { Image } from '@ui/Image';
import { Button } from '@ui/Button';
import { PhoneVerificationModalContainer } from '@containers/PhoneVerificationModal';
import { SubscriptionRequiredErrorModal } from '@components/SubscriptionRequiredErrorModal';

import s from './UnpaidOrdersNotify.sss';

export interface SmartPlan {
  planId: string,
  secondTitle?: string | null,
}

type Order = {
  id: number;
  dialog?: {
    id: number;
    previewImages?: string[] | null;
  } | null
  paymentAmount?: string | null;
  paymentTransactionStatus?: {
    status: PaymentTransactionStatusEnum,
  } | null,
  paymentDetails: {
    status: PaymentStatusEnum,
  },
  smartPlanId?: string | null,
  price?: number,
  paymentMethod?: {
    id: PaymentMethodEnum,
  } | null,
};

type UnpaidOrdersNotifyProps = {
  order: Order;
  onClose: () => void;
  className?: string
  smartPlan?: SmartPlan | null
  loading?: boolean,
};

export const UnpaidOrdersNotify: React.FC<UnpaidOrdersNotifyProps> = ({
  order,
  onClose,
  className,
  smartPlan,
  loading,
}) => {
  const { currencyName } = useRegion();
  const history = useHistory();
  const [isPhoneVerificationModalOpen, setIsPhoneVerificationModalOpen] = useState(false);
  const [isSubscriptionRequiredErrorModalOpen, setIsSubscriptionRequiredErrorModalOpen] = useState(false);

  const {
    loading: platformAuthorizeInfoLoading,
    checkIsNeedPhoneVerification,
  } = usePlatformAuthorizeInfo();

  const orderPaymentPathname = getOrderPaymentPathname({
    orderId: order.id,
    paymentAmount: order.paymentAmount,
    paymentStatus: order.paymentDetails.status,
  });

  const handleRedirectToSubscriptionPayment = () => {
    if (smartPlan) {
      const smartPaymantPatnname = generatePath(smartPayment, {
        planId: smartPlan.planId,
      });

      window.location.href = history.createHref({
        pathname: smartPaymantPatnname,
        search: queryString.stringify({
          next: orderPaymentPathname,
          skipSuccess: true,
        }),
      });
    }
  };

  const handlePayClick = async () => {
    const isNeedPhoneVerification = await checkIsNeedPhoneVerification();

    if (isNeedPhoneVerification) {
      setIsPhoneVerificationModalOpen(true);
    } else if (order.paymentTransactionStatus?.status === PaymentTransactionStatusEnum.PendingSubscription) {
      setIsSubscriptionRequiredErrorModalOpen(true);
    } else {
      window.location.href = history.createHref({
        pathname: orderPaymentPathname,
      });
    }
  };

  const images = order?.dialog?.previewImages ? order?.dialog.previewImages.slice(0, 2) : [];
  let imagesComponent;
  if (images.length === 0) {
    imagesComponent = <div className={s.productImagePlaceholder} />;
  } else if (images.length === 1) {
    imagesComponent = (
      <Image
        className={s.productImage}
        src={images[0]}
      />
    );
  } else {
    imagesComponent = (
      <div className={s.twoImages}>
        <Image
          className={s.firstOrderImage}
          src={images[0]}
        />
        <Image
          className={s.secondOrderImage}
          src={images[1]}
        />
      </div>
    );
  }

  const totalPrice = formatPrecisePrice(
    order.paymentMethod?.id === PaymentMethodEnum.SafePayment
      ? Number(order.paymentAmount || 0)
      : Number(order.price || 0) + Number(order.paymentAmount || 0),
  );

  return (
    <>
      <div className={classNames(s.root, className)}>
        <div className={s.content}>
          <div className={s.imageSide}>
            {imagesComponent}
          </div>
          <div className={s.contentText}>
            <span className={s.unpaidOrderText}>
              {t`У вас є неоплачене замовлення`}
            </span>

            {!!totalPrice && (
              <span>
                {t`на суму`}&nbsp;
                <span className={s.bold}>
                  {totalPrice}&nbsp;{currencyName}
                </span>
              </span>
            )}
          </div>

          <Button
            theme="text"
            className={s.close}
            onClick={onClose}
          >
            <CloseIcon
              className={s.closeIcon}
              width="20"
              height="20"
            />
          </Button>
        </div>
        <div className={s.buttons}>
          <Button
            theme="secondary"
            href={history.createHref({
              pathname: generatePath(purchaseDetails, { id: order.id }),
            })}
          >
            {t`Деталі`}
          </Button>

          <Button
            onClick={handlePayClick}
            disabled={platformAuthorizeInfoLoading || loading}
          >
            {t`Оплатити`}
          </Button>
        </div>
      </div>

      <PhoneVerificationModalContainer
        isOpen={isPhoneVerificationModalOpen}
        onClose={() => setIsPhoneVerificationModalOpen(false)}
        description={t`Щоб оформити замовлення, верифікуйте номер телефону`}
        onVerify={handlePayClick}
      />

      <SubscriptionRequiredErrorModal
        isOpen={isSubscriptionRequiredErrorModalOpen}
        secondTitle={smartPlan?.secondTitle}
        onPay={handleRedirectToSubscriptionPayment}
        onRequestClose={() => setIsSubscriptionRequiredErrorModalOpen(false)}
      />
    </>
  );
};
